"use client";
import { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Typography from "../../atoms/typography";
import { Tabs } from "../../molecules/tabion";
import TabList from "../../molecules/tabion/tabList";
import Tab from "../../molecules/tabion/tab";
import TabPanel from "../../molecules/tabion/tabPanel";
import Slider from "react-slick";
import { SlickNextArrow, SlickPrevArrow } from "../../molecules/carouselHelpers";
import { VARIABLE_VIEW_MORE, VIEW_LESS } from "../../../constants/common/footer";

/**
 * ExploreMore Component
 *
 * @param {Object} props - The component props.
 * @param {Object} props.data - The data Object used to render the component.
 * @param {boolean} [props.isArrowVisibileDesktop=true] - Determines if arrows are visible on desktop.
 * @param {boolean} props.isMobile - Indicates whether the user is on a mobile device.
 * @returns {JSX.Element} The rendered ExploreMore component.
 */
const ExploreMore = ({ data, isMobile }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [showMore, setShowMore] = useState({});

  var desktopSettings = {
    dots: false,
    infinite: false,
    slidesToShow: 6,
    slidesToScroll: 1,
    arrows: true,
    autoplay: false,
    centerMode: false,
    nextArrow: <SlickNextArrow />,
    prevArrow: <SlickPrevArrow />,
  };

  /**
   * Toggles the visibility state for a specific index.
   *
   * @param {string | number} index - The index of the item to toggle.
   * @returns {void}
   */
  const onToggleExpand = (index) => {
    setShowMore((prev) => ({
      ...prev,
      [index]: !prev[index], // Toggle visibility
    }));
  };

  /**
   * Handles tab change event by updating the selected tab state.
   *
   * @callback onTabChange
   * @param {string | number} evt - The identifier of the selected tab.
   * @returns {void}
   */
  const onTabChange = useCallback((evt) => {
    setSelectedTab(evt);
  }, []);

  const tabPanels = useMemo(() => {
    return data.mainList.map((item, tabIndex) => (
      <TabPanel
        selected={selectedTab}
        index={tabIndex}
        key={tabIndex}
        className="md:pt-21 relative bg-gradient-to-b from-fnp-700 to-white-900 px-0 pb-0 pt-16 md:px-20 md:pb-0 xxl:pt-22"
      >
        {isMobile
          ? item?.subList.length > 0 && (
              <ul className="flex w-full gap-40 overflow-x-scroll px-16 scrollbar-none">
                {item?.subList.map((item, sectionIndex) => (
                  <li key={sectionIndex} className="max-w-[165px] flex-none">
                    {item?.title && (
                      <Typography
                        variant=""
                        Tag="h3"
                        classes={`text-14 mb-8 font-600 md:text-16 truncate md:font-600 text-black-100 md:mb-16`}
                      >
                        {item.title}
                      </Typography>
                    )}
                    {item?.anchorList.length > 0 && (
                      <ul className="flex flex-col gap-4">
                        {item?.anchorList.map((item, index) => {
                          if (index <= 2) {
                            return (
                              <li key={index}>
                                <a href={item.url} className="text-14 text-grey-500">
                                  {item.textContent}
                                </a>
                              </li>
                            );
                          }
                        })}

                        <li
                          className={`cursor-pointer text-14 font-500 text-blue-100 ${!showMore[`${tabIndex}-${sectionIndex}`] ? "" : "hidden"}`}
                          onClick={() => onToggleExpand(`${tabIndex}-${sectionIndex}`)}
                        >
                          {VARIABLE_VIEW_MORE(item?.anchorList.length - 3)}
                        </li>
                        <div className={showMore[`${tabIndex}-${sectionIndex}`] ? "" : "hidden"}>
                          {item?.anchorList.map((item, index) => {
                            if (index > 2) {
                              return (
                                <li key={index}>
                                  <a href={item.url} className="text-14 text-grey-500">
                                    {item.textContent}
                                  </a>
                                </li>
                              );
                            }
                          })}
                        </div>
                        <li
                          className={`cursor-pointer text-14 font-500 text-blue-100 ${showMore[`${tabIndex}-${sectionIndex}`] ? "" : "hidden"}`}
                          onClick={() => onToggleExpand(`${tabIndex}-${sectionIndex}`)}
                        >
                          {VIEW_LESS}
                        </li>
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )
          : item?.subList.length > 0 && (
              <Slider {...desktopSettings}>
                {item?.subList.map((item, sectionIndex) => (
                  <div key={sectionIndex}>
                    {item?.title && (
                      <Typography variant="" Tag="h3" classes={`text-16 truncate font-600 text-black-100 mb-10`}>
                        {item.title}
                      </Typography>
                    )}
                    {item?.anchorList.length > 0 && (
                      <ul className="flex flex-col gap-4">
                        {item?.anchorList.map((item, index) => {
                          if (index <= 2) {
                            return (
                              <li key={index}>
                                <a href={item.url} className="text-14 text-grey-500">
                                  {item.textContent}
                                </a>
                              </li>
                            );
                          }
                        })}

                        <li
                          className={`cursor-pointer text-14 font-600 text-blue-100 ${!showMore[`${tabIndex}-${sectionIndex}`] ? "" : "hidden"}`}
                          onClick={() => onToggleExpand(`${tabIndex}-${sectionIndex}`)}
                        >
                          {VARIABLE_VIEW_MORE(item?.anchorList.length - 3)}
                        </li>
                        <div className={showMore[`${tabIndex}-${sectionIndex}`] ? "" : "hidden"}>
                          {item?.anchorList.map((item, index) => {
                            if (index > 2) {
                              return (
                                <li key={index}>
                                  <a href={item.url} className="text-14 text-grey-500">
                                    {item.textContent}
                                  </a>
                                </li>
                              );
                            }
                          })}
                        </div>
                        <li
                          className={`cursor-pointer text-14 font-600 text-blue-100 ${showMore[`${tabIndex}-${sectionIndex}`] ? "" : "hidden"}`}
                          onClick={() => onToggleExpand(`${tabIndex}-${sectionIndex}`)}
                        >
                          {VIEW_LESS}
                        </li>
                      </ul>
                    )}
                  </div>
                ))}
              </Slider>
            )}
      </TabPanel>
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.data, selectedTab, showMore]);
  return (
    <>
      <div className="flex w-full flex-col border-b-2 border-lightGrey bg-white-900 px-0 py-20 pb-18 md:px-40 md:pt-0">
        <Typography
          variant="subheading4"
          Tag="h2"
          classes="mb-8 mx-16 overflow-hidden text-ellipsis pl-2 text-left text-16 font-600 capitalize md:mb-16 md:text-24 md:mx-0"
        >
          {data.title}
        </Typography>
        <div className="w-full">
          <Tabs onClick={onTabChange}>
            <TabList
              selected={selectedTab}
              onChange={onTabChange}
              onTabSelected={onTabChange}
              tabCount={data.mainList.length}
              isBottomRound={true}
            >
              {data.mainList.map((item, index) => {
                return (
                  <Tab bgStyleClass="relative p-16 rounded-t-xl sm:flex-1 w-1/2 min-w-[150px]" key={index}>
                    <div className="flex w-full flex-col items-center justify-center gap-8 text-18">
                      <Typography
                        variant=""
                        Tag="p"
                        classes={`text-14 ${selectedTab === index ? "font-600 text-fnp-400" : "font-400 text-grey-400"} text-nowrap xl:text-16 xxl:text-18`}
                      >
                        {item.title}
                      </Typography>
                    </div>
                  </Tab>
                );
              })}
            </TabList>
            {tabPanels}
          </Tabs>
        </div>
      </div>
    </>
  );
};

ExploreMore.propTypes = {
  data: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

export default ExploreMore;
